import React from 'react';
import { type CustomerResponse } from '../../lib/api';

export interface ReceiptData {
  cart: any[];
  subtotal: number;
  tax: number;
  total: number;
  config: any;
  customer?: CustomerResponse | null;
  paymentMethod: string;
  amountInput: string;
  cashierName: string;
  orderId?: string | null;
  store?: any;
  location?: any;
  sessionId?: string | null;
}

export function ReceiptPreview({ 
  cart, 
  subtotal: _subtotal, 
  tax: _tax, 
  total, 
  config,
  customer,
  paymentMethod,
  amountInput,
  cashierName,
  orderId,
  store,
  location,
  sessionId
}: { 
  cart: any[], 
  subtotal: number, 
  tax: number, 
  total: number,
  config: any,
  customer?: CustomerResponse | null,
  paymentMethod: string,
  amountInput: string,
  cashierName: string,
  orderId?: string | null,
  store?: any,
  location?: any,
  sessionId?: string | null
}) {
  const activeStore = store || location;
  const date = new Date().toLocaleString();
  const totalQty = cart.reduce((sum, l) => sum + l.qty, 0);
  const discountTotal = cart.reduce((sum, l) => sum + l.discount_amount * l.qty, 0);
  const cashPaid = paymentMethod === 'cash' ? (amountInput ? parseFloat(amountInput) : total) : 0;
  const changeGiven = paymentMethod === 'cash' && amountInput ? Math.max(0, cashPaid - total) : 0;
  const mpesaPaid = paymentMethod === 'mobile_money' ? total : 0;

  return (
    <div className="printable-receipt" style={{ 
      background: 'white', 
      width: '100%', 
      maxWidth: '380px', 
      margin: '1rem auto', 
      padding: '20px 15px', 
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      fontFamily: '"Courier New", Courier, monospace',
      color: 'black',
      fontSize: '12px',
      lineHeight: '1.2'
    }}>
      {/* HEADER */}
      <div style={{ textAlign: 'center', marginBottom: '10px', textTransform: 'uppercase' }}>
        <div style={{ fontWeight: 800, fontSize: '14px' }}>{config?.businessName || ''}</div>
        <div>{config?.branchName || activeStore?.name || ''}</div>
        {(config?.contactAddress || '') && <div style={{ fontSize: '11px' }}>{config.contactAddress}</div>}
        {(config?.contactPhone || config?.contactEmail) && (
          <div style={{ fontSize: '11px' }}>
            {config?.contactPhone ? `TEL: ${config.contactPhone}` : ''}{config?.contactPhone && config?.contactEmail ? ' ' : ''}{config?.contactEmail || ''}
          </div>
        )}
        <div style={{ fontWeight: 800, margin: '5px 0', fontSize: '14px' }}>
          {paymentMethod === 'mobile_money' ? 'MPESA SALE'
           : paymentMethod === 'credit' ? 'CREDIT SALE'
           : paymentMethod === 'bank' ? 'BANK SALE'
           : 'CASH SALE'}
        </div>
        {activeStore?.vat_config?.mode !== 'disabled' && (
          <div style={{ fontSize: '11px' }}>VAT Reg: {config?.vatPin || activeStore?.vat_config?.pin || ''} PIN No. {config?.vatPin || activeStore?.vat_config?.pin || ''}</div>
        )}
        <div style={{ fontSize: '11px' }}>Date: {date}</div>
        <div style={{ fontSize: '11px' }}>Branch: {config?.branchNo || '1'} Till: {config?.tillNo || '1'} Session: {sessionId?.slice(-4) || '---'} Rct: {orderId?.slice(-4) || '---'}</div>
        <div style={{ fontSize: '11px' }}>Served by: {cashierName ? cashierName.split(' ')[0] : 'Cashier'}</div>
        <div style={{ margin: '5px 0', fontSize: '10px', letterSpacing: '2px' }}>||||||||||||||||||||||||||||||</div>
        <div style={{ fontSize: '10px' }}>{orderId || '---'}</div>
      </div>

      <div style={{ fontWeight: 800, marginBottom: '10px' }}>CUSTOMER: {customer?.name || 'WALK-IN'}</div>

      {/* ITEMS TABLE */}
      <div style={{ borderTop: '1px dashed #000', borderBottom: '1px dashed #000', padding: '5px 0', marginBottom: '5px' }}>
        <div style={{ display: 'flex', fontWeight: 800, marginBottom: '5px' }}>
          <div style={{ flex: 2 }}>Item</div>
          <div style={{ flex: 1, textAlign: 'right' }}>Qty</div>
          <div style={{ flex: 1, textAlign: 'right' }}>Each</div>
          <div style={{ flex: 1, textAlign: 'right' }}>Total</div>
        </div>
        {cart.map(l => (
          <React.Fragment key={l.product.id}>
            <div style={{ marginBottom: '8px' }}>
              <div style={{ fontWeight: 600 }}>{l.product.name}</div>
              <div style={{ fontSize: '10px', color: '#666' }}>{l.product.sku}</div>
              <div style={{ display: 'flex' }}>
                <div style={{ flex: 2 }}></div>
                <div style={{ flex: 1, textAlign: 'right' }}>{l.qty.toFixed(3)} PC</div>
                <div style={{ flex: 1, textAlign: 'right' }}>{(l.unit_price - l.discount_amount).toFixed(2)}</div>
                <div style={{ flex: 1, textAlign: 'right' }}>{(l.qty * (l.unit_price - l.discount_amount)).toFixed(2)}</div>
              </div>
            </div>
            {l.service_charge > 0 && (
              <div style={{ marginBottom: '8px' }}>
                <div style={{ fontWeight: 600 }}>Service Charge ({l.product.name})</div>
                <div style={{ fontSize: '10px', color: '#666' }}>SERVICE</div>
                <div style={{ display: 'flex' }}>
                  <div style={{ flex: 2 }}></div>
                  <div style={{ flex: 1, textAlign: 'right' }}>1 PC</div>
                  <div style={{ flex: 1, textAlign: 'right' }}>{l.service_charge.toFixed(2)}</div>
                  <div style={{ flex: 1, textAlign: 'right' }}>{l.service_charge.toFixed(2)}</div>
                </div>
              </div>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* TOTALS BLOCK */}
      <div style={{ marginBottom: '10px' }}>
        {discountTotal > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', color: '#dc2626' }}>
            <span>DISCOUNT</span>
            <span>-{discountTotal.toFixed(2)}</span>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800, fontSize: '14px' }}>
          <span>TOTAL</span>
          <span>{total.toFixed(2)}</span>
        </div>
        {cashPaid > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>CASH PAID</span>
            <span>{cashPaid.toFixed(2)}</span>
          </div>
        )}
        {changeGiven > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, color: '#064734' }}>
            <span>CHANGE</span>
            <span>{changeGiven.toFixed(2)}</span>
          </div>
        )}
        {mpesaPaid > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>MPESA PAY</span>
            <span>{mpesaPaid.toFixed(2)}</span>
          </div>
        )}
        <div style={{ marginTop: '5px', fontSize: '11px' }}>
          <div>Total Qty : {totalQty.toFixed(2)} units</div>
        </div>
      </div>

      {/* MPESA DETAILS BOX */}
      {paymentMethod === 'mobile_money' && (
        <div style={{ border: '1px solid #000', padding: '5px', margin: '10px 0', fontSize: '11px' }}>
          <div style={{ fontWeight: 800, borderBottom: '1px solid #000', marginBottom: '3px', paddingBottom: '2px' }}>MPESA DETAILS</div>
          <div style={{ display: 'flex' }}><div style={{ width: '100px' }}>Name</div><div>: {customer?.name || 'WALK-IN'}</div></div>
          <div style={{ display: 'flex' }}><div style={{ width: '100px' }}>Mobile No.</div><div>: {customer?.phone || 'N/A'}</div></div>
          <div style={{ display: 'flex' }}><div style={{ width: '100px' }}>MPESA Trn #</div><div>: {orderId?.slice(0, 8).toUpperCase() || 'N/A'}</div></div>
          <div style={{ display: 'flex' }}><div style={{ width: '100px' }}>Our Trn ID #</div><div>: {orderId?.slice(-7) || 'N/A'}</div></div>
          <div style={{ display: 'flex' }}><div style={{ width: '100px' }}>Amount</div><div>: {total.toFixed(2)}</div></div>
        </div>
      )}

      {/* VAT SUMMARY */}
      {activeStore?.vat_config?.mode !== 'disabled' && (
        <div style={{ margin: '10px 0' }}>
          <div style={{ display: 'flex', borderBottom: '1px solid #000', paddingBottom: '2px', fontWeight: 800 }}>
            <div style={{ flex: 1 }}>CODE</div>
            <div style={{ flex: 1, textAlign: 'right' }}>PRE-VAT</div>
            <div style={{ flex: 1, textAlign: 'right' }}>VAT</div>
            <div style={{ flex: 1, textAlign: 'right' }}>TOTAL</div>
          </div>
          <div style={{ display: 'flex', paddingTop: '2px' }}>
            <div style={{ flex: 1 }}>G - {Number(activeStore?.vat_config?.rate || 16).toFixed(2)}%</div>
            <div style={{ flex: 1, textAlign: 'right' }}>{(total / (1 + Number(activeStore?.vat_config?.rate || 16)/100)).toFixed(2)}</div>
            <div style={{ flex: 1, textAlign: 'right' }}>{(total - (total / (1 + Number(activeStore?.vat_config?.rate || 16)/100))).toFixed(2)}</div>
            <div style={{ flex: 1, textAlign: 'right' }}>{total.toFixed(2)}</div>
          </div>
          <div style={{ display: 'flex', fontWeight: 800, marginTop: '2px', borderTop: '1px solid #000' }}>
            <div style={{ flex: 1 }}>TOTALS</div>
            <div style={{ flex: 1, textAlign: 'right' }}>{(total / (1 + Number(activeStore?.vat_config?.rate || 16)/100)).toFixed(2)}</div>
            <div style={{ flex: 1, textAlign: 'right' }}>{(total - (total / (1 + Number(activeStore?.vat_config?.rate || 16)/100))).toFixed(2)}</div>
            <div style={{ flex: 1, textAlign: 'right' }}>{total.toFixed(2)}</div>
          </div>
        </div>
      )}

      {/* FOOTER */}
      <div style={{ textAlign: 'center', marginTop: '15px' }}>
        {config?.footerNote && <div style={{ marginBottom: '5px' }}>{config.footerNote}</div>}
        <div style={{ fontSize: '9px', fontWeight: 600, marginBottom: '5px' }}>Goods once sold are not refundable</div>
        {config?.tagline && <div style={{ fontStyle: 'italic', marginBottom: '10px' }}>"{config.tagline}"</div>}
        
        <div style={{ fontWeight: 800, marginBottom: '5px' }}>CONTROL UNIT INFO</div>
        <div style={{ fontSize: '10px' }}>
          <div>CU No: {config?.kraCuNo || ''}</div>
          <div>CU Inv No: {config?.kraCuInvNo || ''}</div>
          <div>Rct Ref No: {config?.kraRctRefNo || ''}{orderId?.slice(-4) || ''}</div>
          <div>Date: {date}</div>
        </div>
        
        <div style={{ marginTop: '15px', fontSize: '10px', color: '#666', borderTop: '1px solid #eee', paddingTop: '10px' }}>
          Powered By Dukene<br/>
          dukene.co.ke
        </div>
      </div>
    </div>
  );
}

export async function printReceipt(data: ReceiptData, printerService: any) {
  try {
    if (printerService && printerService.getStatus().connected) {
      await printerService.kickDrawer();
    }
    if (data.orderId) {
      console.info('Printing receipt:', data.orderId);
    }
    window.print();
  } catch (err) {
    console.error('Failed to print receipt:', err);
    window.print();
  }
}
